import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { FileText, Eye, Edit, Trash2, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import FormRendererBranded from './FormRendererBranded';
import FormSelector from './FormSelector';

const ELEGANT_GOLD = '#C9A961';

const FormsList = ({ recordType, recordId, canEdit = true }) => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formResponse, setFormResponse] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState('view'); // 'view' or 'edit'
  const [showTemplates, setShowTemplates] = useState(false);
  const [allTemplates, setAllTemplates] = useState([]);

  useEffect(() => {
    fetchForms();
    fetchAllTemplates();
  }, [recordType, recordId]);

  const fetchAllTemplates = async () => {
    try {
      const response = await api.get('/forms/templates');
      setAllTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchForms = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/forms/record/${recordType}/${recordId}`);
      setForms(response.data);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = async (form, mode = 'view') => {
    setSelectedForm(form);
    setViewMode(mode);
    
    try {
      // Fetch the template
      const templateResponse = await api.get(`/forms/templates/${form.template_id}`);
      setSelectedTemplate(templateResponse.data);
      
      // Fetch the latest response if exists
      try {
        const responseData = await api.get(`/forms/responses/${form.record_form_id}/latest`);
        setFormResponse(responseData.data);
      } catch (error) {
        // No response exists yet
        setFormResponse(null);
      }
      
      setDialogOpen(true);
    } catch (error) {
      console.error('Failed to load form:', error);
      toast.error('Failed to load form details');
    }
  };

  const handleDeleteForm = async (formId) => {
    if (!confirm('Are you sure you want to remove this form?')) return;
    
    try {
      await api.delete(`/forms/record/${formId}`);
      toast.success('Form removed successfully');
      fetchForms();
    } catch (error) {
      console.error('Failed to delete form:', error);
      toast.error('Failed to remove form');
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      // Submit form response to backend
      const response = await api.post('/forms/responses', {
        record_form_id: selectedForm.record_form_id,
        responses: formData,
        status: 'submitted',
        template_id: selectedTemplate.template_id,
        template_name: selectedTemplate.template_name,
        record_type: recordType,
        record_id: recordId
      });
      
      setFormResponse(response.data);
      setDialogOpen(false);
      toast.success('Form submitted successfully!');
      fetchForms();
    } catch (error) {
      console.error('Failed to submit form:', error);
      toast.error('Failed to submit form');
    }
  };

  const handleFormUpdate = (response) => {
    setFormResponse(response);
    toast.success('Form updated successfully!');
  };

  const getFormStatus = (form) => {
    // This would need to check if there's a response
    // For now, we'll use a simple check
    return formResponse ? 'filled' : 'empty';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-[#C9A961] border-t-transparent rounded-full mx-auto"></div>
        <p className="text-gray-400 mt-4">Loading forms...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-[#C9A961] flex items-center gap-2">
          <FileText className="h-6 w-6" />
          Forms ({forms.length})
        </h3>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowTemplates(!showTemplates)}
            className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/20"
          >
            <Eye className="h-4 w-4 mr-2" />
            {showTemplates ? 'Hide Templates' : 'Browse Templates'}
          </Button>
          {canEdit && (
            <FormSelector
              recordType={recordType}
              recordId={recordId}
              onFormAdded={fetchForms}
            />
          )}
        </div>
      </div>

      {/* Available Templates Section */}
      {showTemplates && (
        <div className="bg-black/40 rounded-xl border-2 border-[#C9A961]/30 p-6">
          <h4 className="text-xl font-bold text-[#C9A961] mb-4">
            Available Form Templates ({allTemplates.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {allTemplates.map((template) => (
              <Card
                key={template.template_id}
                className="bg-black/60 border border-[#C9A961]/30 hover:border-[#C9A961]/60 transition-all"
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h5 className="text-white font-semibold text-sm flex items-center gap-1">
                        <FileText className="h-4 w-4 text-[#C9A961]" />
                        {template.template_name}
                      </h5>
                      <p className="text-gray-400 text-xs mt-1 line-clamp-2">
                        {template.description || 'No description'}
                      </p>
                      <p className="text-gray-500 text-xs mt-2">
                        {template.fields?.length || 0} fields • {template.category}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Forms List */}
      {forms.length === 0 ? (
        <div className="text-center py-16 bg-black/40 rounded-xl border border-[#C9A961]/30">
          <FileText className="h-16 w-16 text-[#C9A961]/30 mx-auto mb-4" />
          <p className="text-gray-400 text-lg mb-2">No forms attached yet</p>
          <p className="text-gray-500 text-sm">Click "Add Form" to attach a template to this {recordType}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {forms.map((form) => (
            <Card
              key={form.record_form_id}
              className="bg-black/40 border-2 border-[#C9A961]/30 hover:border-[#C9A961]/60 transition-all"
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-white flex items-center gap-2">
                      <FileText className="h-5 w-5 text-[#C9A961]" />
                      {form.template_name}
                    </h4>
                    <div className="flex items-center gap-2 mt-2">
                      {formResponse ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-[#C9A961]" />
                          <span className="text-sm text-[#C9A961]">Filled</span>
                          <span className="text-xs text-gray-500">
                            • {formResponse.filled_by_name}
                          </span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-4 w-4 text-yellow-400" />
                          <span className="text-sm text-yellow-400">Not filled</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  {formResponse ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenForm(form, 'view')}
                        className="flex-1 border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/20"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenForm(form, 'edit')}
                          className="flex-1 border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/20"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      )}
                    </>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleOpenForm(form, 'edit')}
                      className="flex-1 bg-[#C9A961] text-black hover:bg-[#D4C278]"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Fill Form
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteForm(form.record_form_id)}
                      className="border-red-500 text-red-500 hover:bg-red-950"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-black/95 border-2 border-[#C9A961] backdrop-blur-xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#C9A961] flex items-center gap-2">
              <FileText className="h-6 w-6" />
              {viewMode === 'view' ? 'View Form' : 'Fill Form'}
            </DialogTitle>
          </DialogHeader>

          {selectedTemplate && (
            <div className="mt-4">
              {viewMode === 'edit' ? (
                <FormRendererBranded
                  template={selectedTemplate}
                  onSubmit={(formData) => handleFormSubmit(formData)}
                  initialData={formResponse?.responses || {}}
                  readOnly={false}
                />
              ) : (
                // View-only mode with branded renderer
                <FormRendererBranded
                  template={selectedTemplate}
                  initialData={formResponse?.responses || {}}
                  readOnly={true}
                  onSubmit={() => {}}
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FormsList;
